import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { useToast } from '@/components/ui/use-toast';
import type { ScrollContext } from '@/utils/scrollUtils';
import { delayedScrollToTop } from '@/utils/scrollUtils';

export type GalleryActionsOptions = {
  refetch: () => Promise<unknown>;
  resetToInitialState?: () => void;
  scrollContext?: ScrollContext;
  enableToasts?: boolean;
};

function useGalleryActions(options: GalleryActionsOptions) {
  const { toast } = useToast();
  const translate = useTranslations();
  const { refetch, resetToInitialState, scrollContext, enableToasts = true } = options;

  /**
   * Centralized upload completion handler
   * Handles: reset state -> show toast -> refetch -> scroll to top
   */
  const handleUploadComplete = useCallback(async () => {
    // Reset all state to initial values
    if (resetToInitialState) {
      resetToInitialState();
    }

    // Show processing toast
    if (enableToasts) {
      toast({
        title: translate('gallery.toast.processingTitle'),
        description: translate('gallery.toast.processingDescription'),
        variant: 'primary',
      });
    }

    // Refetch data
    await refetch();

    // Scroll to top with delay to ensure data is loaded
    delayedScrollToTop(scrollContext, 100);
  }, [refetch, resetToInitialState, scrollContext, enableToasts, toast, translate]);

  /**
   * Archive completion handler
   */
  const handleArchiveComplete = useCallback(async (count: number) => {
    if (enableToasts) {
      toast({
        title: translate('gallery.toast.archivedTitle'),
        description: translate('gallery.toast.archivedDescription', { count: String(count) }),
        variant: 'primary',
      });
    }

    // Reset and refresh
    if (resetToInitialState) {
      resetToInitialState();
    }
    await refetch();
    delayedScrollToTop(scrollContext, 0);
  }, [refetch, resetToInitialState, scrollContext, enableToasts, toast, translate]);

  /**
   * Simple refetch and scroll handler
   */
  const handleRefetchAndScroll = useCallback(async () => {
    await refetch();
    delayedScrollToTop(scrollContext, 100);
  }, [refetch, scrollContext]);

  return {
    handleUploadComplete,
    handleArchiveComplete,
    handleRefetchAndScroll,
  };
}

export default useGalleryActions;
